import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { CheckCircle2Icon, PackagePlusIcon, PlusIcon } from 'lucide-react';
import { Button } from './Button';
import { Modal } from './Modal';
import { Select } from './Select';
import { TextInput } from './TextInput';
type WeightType = 'Festgewicht' | 'Verwogen';
type Material = {
  id: string;
  number: string;
  name: string;
  description: string;
  weightType: WeightType;
  fixedWeightKg?: number;
};
type MaterialFormValues = {
  number: string;
  name: string;
  description: string;
  weightType: WeightType | '';
  fixedWeightKg: string;
};
type MaterialFormErrors = {
  number?: string;
  name?: string;
  description?: string;
  weightType?: string;
  fixedWeightKg?: string;
};
const initialMaterials: Material[] = [{
  id: 'material-1042',
  number: 'RM-1042',
  name: 'Zuckerstreusel, bunt',
  description: 'Dekorstreusel für Backwaren',
  weightType: 'Festgewicht',
  fixedWeightKg: 10
}, {
  id: 'material-1043',
  number: 'RM-1043',
  name: 'Weizenmehl Type 405',
  description: 'Universalmehl für Backwaren',
  weightType: 'Verwogen'
}, {
  id: 'material-1044',
  number: 'RM-1044',
  name: 'Butter, gesalzen',
  description: 'Hochwertige Butter für Teige',
  weightType: 'Festgewicht',
  fixedWeightKg: 25
}, {
  id: 'material-1045',
  number: 'RM-1045',
  name: 'Schokoladenstücke, dunkel',
  description: '70 % Kakaoanteil, für Gebäck',
  weightType: 'Verwogen'
}, {
  id: 'material-1046',
  number: 'RM-1046',
  name: 'Vanillezucker',
  description: 'Aromatisierter Zucker',
  weightType: 'Festgewicht',
  fixedWeightKg: 5
}];
const emptyFormValues: MaterialFormValues = {
  number: '',
  name: '',
  description: '',
  weightType: '',
  fixedWeightKg: ''
};
const weightTypeOptions = [{
  value: 'Festgewicht',
  label: 'Festgewicht'
}, {
  value: 'Verwogen',
  label: 'Verwogen'
}];
export function MaterialManagement() {
  const [materials, setMaterials] = useState<Material[]>(initialMaterials);
  const [formValues, setFormValues] = useState<MaterialFormValues>(emptyFormValues);
  const [errors, setErrors] = useState<MaterialFormErrors>({});
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [weightTypeFilter, setWeightTypeFilter] = useState<WeightType | 'all'>('all');
  const normalizedSearchQuery = searchQuery.trim().toLocaleLowerCase('de-DE');
  const filteredMaterials = materials.filter(material => {
    const searchableContent = `${material.number} ${material.name} ${material.description}`.toLocaleLowerCase('de-DE');
    const matchesSearch = !normalizedSearchQuery || searchableContent.includes(normalizedSearchQuery);
    const matchesWeightType = weightTypeFilter === 'all' || material.weightType === weightTypeFilter;
    return matchesSearch && matchesWeightType;
  });
  const hasActiveFilters = Boolean(searchQuery.trim()) || weightTypeFilter !== 'all';
  useEffect(() => {
    const loadingTimeoutId = window.setTimeout(() => setIsLoading(false), 2000);
    return () => window.clearTimeout(loadingTimeoutId);
  }, []);
  useEffect(() => {
    if (!successMessage) {
      return;
    }
    const timeoutId = window.setTimeout(() => setSuccessMessage(''), 4000);
    return () => window.clearTimeout(timeoutId);
  }, [successMessage]);
  function openCreateDialog() {
    setFormValues(emptyFormValues);
    setErrors({});
    setIsDialogOpen(true);
  }
  function handleDialogOpenChange(open: boolean) {
    setIsDialogOpen(open);
    if (!open) {
      setErrors({});
    }
  }
  function resetFilters() {
    setSearchQuery('');
    setWeightTypeFilter('all');
  }
  function updateFormValue<Key extends keyof MaterialFormValues>(key: Key, value: MaterialFormValues[Key]) {
    setFormValues(current => ({
      ...current,
      [key]: value,
      ...(key === 'weightType' && value !== 'Festgewicht' ? {
        fixedWeightKg: ''
      } : {})
    }));
    setErrors(current => ({
      ...current,
      [key]: undefined,
      ...(key === 'weightType' ? {
        fixedWeightKg: undefined
      } : {})
    }));
  }
  function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const normalizedNumber = formValues.number.trim().toUpperCase();
    const nextErrors = validateMaterialForm(formValues, materials);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0 || !formValues.weightType) {
      return;
    }
    setMaterials(currentMaterials => [...currentMaterials, {
      id: `material-${Date.now()}`,
      number: normalizedNumber,
      name: formValues.name.trim(),
      description: formValues.description.trim(),
      weightType: formValues.weightType,
      fixedWeightKg: formValues.weightType === 'Festgewicht' ? parseWeightInKilograms(formValues.fixedWeightKg) : undefined
    }]);
    setSuccessMessage(`${normalizedNumber} wurde als Rohstoff angelegt.`);
    setIsDialogOpen(false);
    setFormValues(emptyFormValues);
  }
  return <section className="min-h-screen w-full bg-surface-subtle font-body text-text-primary" data-id="element-41">
      <header className="border-b border-border bg-surface px-5 py-5 sm:px-8 sm:py-6 lg:px-10" data-id="element-42">
        <motion.div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between" initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.28,
        ease: 'easeOut'
      }} data-id="element-43">
          <div data-id="element-44">
            <h1 className="font-heading text-3xl leading-tight sm:text-4xl" data-id="element-45">
              Rohstoffe
            </h1>
          </div>
          <Button onClick={openCreateDialog} leadingIcon={<PlusIcon size={18} aria-hidden="true" data-id="element-47" />} data-id="element-46">
            Rohstoff anlegen
          </Button>
        </motion.div>
      </header>

      <div className="mx-auto w-full max-w-[1540px] px-5 py-6 sm:px-8 sm:py-8 lg:px-10" data-id="element-48">
        <motion.section className="overflow-hidden rounded-lg border border-border bg-surface shadow-sm" aria-label="Rohstoffübersicht" initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.3,
        ease: 'easeOut'
      }} data-id="element-49">
          <div className="border-b border-border bg-surface px-4 py-4 sm:px-5" data-id="element-50">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end" data-id="element-51">
              <div className="min-w-0 flex-1" data-id="element-52">
                <TextInput id="material-search" name="materialSearch" type="search" label="" aria-label="Rohstoffe durchsuchen" placeholder="Nummer, Name oder Beschreibung" value={searchQuery} onChange={event => setSearchQuery(event.target.value)} data-id="element-53" />
              </div>
              <div className="w-full sm:w-[260px]" data-id="element-54">
                <Select id="material-weight-type-filter" name="weightTypeFilter" label="" aria-label="Gewichtsart filtern" options={[{
                value: 'all',
                label: 'Alle Gewichtsarten'
              }, ...weightTypeOptions]} value={weightTypeFilter} onChange={event => setWeightTypeFilter(event.target.value as WeightType | 'all')} data-id="element-55" />
              </div>
            </div>
          </div>

          <div className="overflow-x-auto" data-id="element-56">
            <table className="w-full min-w-[840px] table-fixed text-left" data-id="element-57">
              <caption className="sr-only" data-id="element-58">
                Übersicht aller angelegten Rohstoffe mit Nummer, Name,
                Beschreibung, Gewicht und Gewichtsart.
              </caption>
              <colgroup data-id="element-59">
                <col className="w-[16%]" data-id="element-60" />
                <col className="w-[23%]" data-id="element-61" />
                <col className="w-[31%]" data-id="element-62" />
                <col className="w-[14%]" data-id="element-63" />
                <col className="w-[16%]" data-id="element-64" />
              </colgroup>
              <thead className="border-b border-border bg-surface-subtle" data-id="element-65">
                <tr className="text-xs font-semibold uppercase tracking-[0.12em] text-text-secondary" data-id="element-66">
                  <th scope="col" className="px-5 py-4" data-id="element-67">
                    Nummer
                  </th>
                  <th scope="col" className="px-4 py-4" data-id="element-68">
                    Name
                  </th>
                  <th scope="col" className="px-4 py-4" data-id="element-69">
                    Beschreibung
                  </th>
                  <th scope="col" className="px-4 py-4" data-id="element-70">
                    Gewicht
                  </th>
                  <th scope="col" className="px-4 py-4" data-id="element-71">
                    Gewichtsart
                  </th>
                </tr>
              </thead>
              <tbody aria-label="Rohstoffübersicht" aria-busy={isLoading} data-id="element-72">
                {isLoading ? <>
                    <tr className="sr-only" data-id="element-73">
                      <td colSpan={5} data-id="element-74">Rohstoffe werden geladen.</td>
                    </tr>
                    {Array.from({
                  length: 6
                }, (_, index) => <tr key={`material-skeleton-${index}`} aria-hidden="true" className="border-b border-border last:border-b-0" data-id="element-75">
                          <td className="px-5 py-4" data-id="element-76">
                            <div className="h-4 w-20 animate-pulse rounded bg-surface-subtle" data-id="element-77" />
                          </td>
                          <td className="px-4 py-4" data-id="element-78">
                            <div className="h-4 w-28 animate-pulse rounded bg-surface-subtle" data-id="element-79" />
                          </td>
                          <td className="px-4 py-4" data-id="element-80">
                            <div className="h-4 w-4/5 animate-pulse rounded bg-surface-subtle" data-id="element-81" />
                          </td>
                          <td className="px-4 py-4" data-id="element-82">
                            <div className="h-4 w-14 animate-pulse rounded bg-surface-subtle" data-id="element-83" />
                          </td>
                          <td className="px-4 py-4" data-id="element-84">
                            <div className="h-6 w-28 animate-pulse rounded bg-surface-subtle" data-id="element-85" />
                          </td>
                        </tr>)}
                  </> : filteredMaterials.length > 0 ? filteredMaterials.map((material, index) => <motion.tr key={material.id} className="border-b border-border last:border-b-0 hover:bg-surface-subtle" initial={{
                opacity: 0,
                y: 8
              }} animate={{
                opacity: 1,
                y: 0
              }} transition={{
                duration: 0.22,
                delay: index * 0.045,
                ease: 'easeOut'
              }} data-id="element-86">
                      <th scope="row" className="whitespace-nowrap px-5 py-4 align-middle font-heading text-sm text-text-primary" data-id="element-87">
                        {material.number}
                      </th>
                      <td className="px-4 py-4 align-middle font-medium text-text-primary" data-id="element-88">
                        {material.name}
                      </td>
                      <td className="px-4 py-4 align-middle text-sm text-text-secondary" data-id="element-89">
                        {material.description}
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 align-middle font-heading text-sm text-text-primary" data-id="element-90">
                        {material.fixedWeightKg !== undefined ? `${formatKilograms(material.fixedWeightKg)} kg` : '—'}
                      </td>
                      <td className="px-4 py-4 align-middle" data-id="element-91">
                        <WeightTypeBadge weightType={material.weightType} data-id="element-92" />
                      </td>
                    </motion.tr>) : <tr data-id="element-93">
                    <td colSpan={5} className="px-5 py-10 text-center" data-id="element-94">
                      <p className="font-heading text-base text-text-primary" data-id="element-95">
                        Keine passenden Rohstoffe gefunden
                      </p>
                      <p className="mt-1 text-sm text-text-secondary" data-id="element-96">
                        Passen Sie die Suche oder den Filter an.
                      </p>
                      <Button type="button" variant="ghost" size="sm" className="mt-3" onClick={resetFilters} data-id="element-97">
                        Filter zurücksetzen
                      </Button>
                    </td>
                  </tr>}
              </tbody>
            </table>
          </div>
        </motion.section>
        <p className="mt-3 text-xs text-text-secondary sm:hidden" data-id="element-98">
          Für weitere Spalten horizontal scrollen.
        </p>
      </div>

      <Modal open={isDialogOpen} onOpenChange={handleDialogOpenChange} title="Rohstoff anlegen" description="Stammdaten und Gewichtsart für den neuen Rohstoff festlegen." size="md" closeLabel="Dialog zum Anlegen eines Rohstoffs schließen" footer={<>
            <Button type="button" variant="secondary" onClick={() => setIsDialogOpen(false)} data-id="element-100">
              Abbrechen
            </Button>
            <Button type="submit" form="material-management-form" data-id="element-101">
              Rohstoff anlegen
            </Button>
          </>} data-id="element-99">
        <form id="material-management-form" noValidate onSubmit={handleSubmit} data-id="element-102">
          <div className="space-y-5" data-id="element-103">
            <TextInput id="material-number" name="number" label="Nummer" placeholder="z. B. RM-1047" value={formValues.number} onChange={event => updateFormValue('number', event.target.value)} error={errors.number} hint="Die Nummer wird automatisch in Großbuchstaben gespeichert." required data-id="element-104" />
            <TextInput id="material-name" name="name" label="Name" placeholder="Rohstoffbezeichnung" value={formValues.name} onChange={event => updateFormValue('name', event.target.value)} error={errors.name} required data-id="element-105" />
            <TextInput id="material-description" name="description" label="Beschreibung" placeholder="Kurze Beschreibung des Rohstoffs" value={formValues.description} onChange={event => updateFormValue('description', event.target.value)} error={errors.description} required data-id="element-106" />
            <Select id="material-weight-type" name="weightType" label="Gewichtsart" placeholder="Gewichtsart auswählen" options={weightTypeOptions} value={formValues.weightType} onChange={event => updateFormValue('weightType', event.target.value as MaterialFormValues['weightType'])} error={errors.weightType} helperText="Festgewicht wird als feste Einheit geführt. Verwogen wird bei der Verarbeitung gewogen." required data-id="element-107" />
            {formValues.weightType === 'Festgewicht' ? <TextInput id="material-fixed-weight" name="fixedWeightKg" type="text" inputMode="decimal" label="Festgewicht in kg" placeholder="z. B. 0,25" value={formValues.fixedWeightKg} onChange={event => updateFormValue('fixedWeightKg', event.target.value)} error={errors.fixedWeightKg} hint="Gewicht einer festen Einheit, etwa einer Packung." required data-id="element-108" /> : null}
          </div>
        </form>
      </Modal>

      <AnimatePresence data-id="element-109">
        {successMessage ? <motion.div role="status" aria-live="polite" className="fixed right-5 top-5 z-30 flex max-w-sm items-start gap-3 rounded-lg border border-success bg-surface p-4 shadow-md sm:right-8 sm:top-8" initial={{
        opacity: 0,
        y: -10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -10
      }} transition={{
        duration: 0.22,
        ease: 'easeOut'
      }} data-id="element-110">
            <CheckCircle2Icon size={20} className="mt-0.5 shrink-0 text-success" aria-hidden="true" data-id="element-111" />
            <div data-id="element-112">
              <p className="font-heading text-sm text-text-primary" data-id="element-113">
                Rohstoff angelegt
              </p>
              <p className="mt-1 text-xs leading-5 text-text-secondary" data-id="element-114">
                {successMessage}
              </p>
            </div>
          </motion.div> : null}
      </AnimatePresence>
    </section>;
}
type WeightTypeBadgeProps = {
  weightType: WeightType;
};
function WeightTypeBadge({
  weightType
}: WeightTypeBadgeProps) {
  const isFixedWeight = weightType === 'Festgewicht';
  return <span className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1.5 text-xs font-semibold ${isFixedWeight ? 'border-success/30 bg-success/15 text-success' : 'border-warning/30 bg-warning/15 text-warning'}`} data-id="element-115">
      <PackagePlusIcon size={14} aria-hidden="true" data-id="element-116" />
      {weightType}
    </span>;
}
function validateMaterialForm(values: MaterialFormValues, materials: Material[]): MaterialFormErrors {
  const errors: MaterialFormErrors = {};
  const normalizedNumber = values.number.trim().toUpperCase();
  if (!normalizedNumber) {
    errors.number = 'Bitte geben Sie eine Rohstoffnummer ein.';
  } else if (materials.some(material => material.number.toUpperCase() === normalizedNumber)) {
    errors.number = 'Diese Rohstoffnummer ist bereits vergeben.';
  }
  if (!values.name.trim()) {
    errors.name = 'Bitte geben Sie einen Namen ein.';
  }
  if (!values.description.trim()) {
    errors.description = 'Bitte geben Sie eine Beschreibung ein.';
  }
  if (!values.weightType) {
    errors.weightType = 'Bitte wählen Sie eine Gewichtsart aus.';
  }
  if (values.weightType === 'Festgewicht' && !isPositiveWeight(values.fixedWeightKg)) {
    errors.fixedWeightKg = 'Bitte geben Sie ein Festgewicht größer als 0 kg ein.';
  }
  return errors;
}
function isPositiveWeight(value: string) {
  const weight = parseWeightInKilograms(value);
  return Number.isFinite(weight) && weight > 0;
}
function parseWeightInKilograms(value: string) {
  return Number(value.trim().replace(',', '.'));
}
function formatKilograms(weightKg: number) {
  return weightKg.toLocaleString('de-DE', {
    maximumFractionDigits: 3
  });
}